// typescript
import React from 'react';
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';
import theme from '@/mui/theme';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { getIconComponentByName } from '@/utils/icons-map';
import FormGroup from '@mui/material/FormGroup';
import { idFromString } from '@/utils/miscellaneous';
import FilterSlider from './FilterSlider';
import mapFacetsToCheckbox from './FilterCheckbox';
import { FilterAccordionData } from '@/types/common';

interface FilterAccordionProps {
  index: number;
  accordionData: FilterAccordionData;
  facets: any;
  storeData: {
    storeId: string;
    langId: string;
    catalogId: string;
    locale: string;
    categoryId: string;
    pageNumber: string;
    pageSize: string;
  };
  handleFilter: Function;
}

interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-detail-tab-${index}`}
      aria-labelledby={`product-detail-tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { sm: '17px 18px', xs: '19px 18px 37px' } }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
};

const StyledTitle = styled(Typography)({
  color: '#000000',
  fontSize: '16px',
  fontWeight: '800',
  letterSpacing: '0.1px',
  lineHeight: '24px',
  paddingLeft: '6px',
});

const StyledAccordion = styled(Accordion)({
  backgroundColor: '#f1f1f1',
  border: '0',
  boxShadow: 'unset',
  marginBottom: '3px',
  maxWidth: '100%',
  '.MuiAccordionDetails-root': {
    padding: '0',
    backgroundColor: '#ffffff',
  },
  '&::before': {
    display: 'none',
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: '0 20px',
  "&.Mui-expanded .Mui-expanded": {
    transform: "rotate(0deg)",
  },
});

const StyledTabPanel = styled(TabPanel)({
  '&.tab-0': {
    '.MuiTableHead-root': {
      'th': {
        '&:last-of-type, &:nth-last-of-type(2)': {
          textAlign: 'center',
        },
      },
    },
  },
  '&.tab-1': {
    [theme.breakpoints.up('sm')]: {
      '.MuiTableHead-root': {
        'th': {
          minWidth: '200px',
        },
      },
    },
    '.MuiTableBody-root': {
      'th:first-of-type': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  },
});

const FilterAccordion: React.FC<FilterAccordionProps> = ({index, accordionData, facets, storeData, handleFilter}) => {
  const [expandedAccordion, setExpandedAccordion] = React.useState(false);

  const handleToggleAccordion = () => {
    setExpandedAccordion(!expandedAccordion);
  };

  return (
    <StyledAccordion square={true} disableGutters={true} key={idFromString(accordionData.accordionTitle)}>
      <StyledAccordionSummary
        onClick={handleToggleAccordion}
        expandIcon={expandedAccordion ? getIconComponentByName('minus') : getIconComponentByName('plus')}
        key={idFromString(accordionData.accordionTitle)}
      >
        <StyledTitle>{accordionData.accordionTitle}</StyledTitle>
      </StyledAccordionSummary>
      <AccordionDetails>
        <StyledTabPanel className={`tab-${index}`} value={index} index={index}>
          <FormGroup>
            {accordionData.accordionCheckbox &&
              accordionData.accordionCheckbox.length > 0 &&
              accordionData.accordionCheckbox.map((singleRowCheckbox, i) =>
                mapFacetsToCheckbox(facets, storeData, handleFilter, singleRowCheckbox, i)
              )}

            {accordionData.accordionSlider && (
              <Box sx={{ mt: 2, maxWidth: '100%' }}>
                <FilterSlider
                  title={accordionData.accordionSlider.label ?? accordionData.accordionTitle}
                  sliderData={accordionData.accordionSlider}
                  storeData={storeData}
                  handleFilter={handleFilter}
                  facets={facets}
                />
              </Box>
            )}
          </FormGroup>
        </StyledTabPanel>
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default FilterAccordion;
